import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { listingImageUrl } from "@/lib/productGallery";

const WishlistCtx = createContext(null);

function key(slug) { return `wishlist:${slug}`; }

export function WishlistProvider({ slug, children }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(key(slug));
      setItems(raw ? JSON.parse(raw) : []);
    } catch {
      setItems([]);
    }
  }, [slug]);

  const save = useCallback((next) => {
    setItems(next);
    localStorage.setItem(key(slug), JSON.stringify(next));
  }, [slug]);

  const has = useCallback((product_id) => items.some((i) => i.product_id === product_id), [items]);

  const toggle = useCallback((product) => {
    const exists = items.some((i) => i.product_id === product.id);
    let next;
    if (exists) {
      next = items.filter((i) => i.product_id !== product.id);
    } else {
      next = [
        ...items,
        {
          product_id: product.id,
          slug: product.slug,
          name: product.name,
          image: listingImageUrl(product.images) || null,
          price: product.price,
          mrp: product.mrp,
          hasVariants: Array.isArray(product.variants) && product.variants.length > 0,
        },
      ];
    }
    save(next);
    return !exists; // true if now added
  }, [items, save]);

  const remove = useCallback((product_id) => save(items.filter((i) => i.product_id !== product_id)), [items, save]);
  const clear = useCallback(() => save([]), [save]);
  const count = useMemo(() => items.length, [items]);

  return (
    <WishlistCtx.Provider value={{ items, has, toggle, remove, clear, count }}>
      {children}
    </WishlistCtx.Provider>
  );
}

export const useWishlist = () => useContext(WishlistCtx);
